import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import api from '../services/apiService';

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const cardStyle = {
  background: '#fff',
  border: '1px solid #d9d9d9',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
};

function getCurrentYearMonth() {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  };
}

function getIsoWeekInfo(dateInput = new Date()) {
  const date = new Date(Date.UTC(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);

  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);

  return {
    year: date.getUTCFullYear(),
    week,
  };
}

function getIsoWeeksInYear(year) {
  return getIsoWeekInfo(new Date(year, 11, 28)).week;
}

function formatMoney(value) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number(value || 0));
}

function formatCount(value) {
  return new Intl.NumberFormat('de-DE').format(Number(value || 0));
}

function formatDelta(value, type) {
  const number = Number(value || 0);
  const prefix = number > 0 ? '+' : '';
  return type === 'money'
    ? `${prefix}${formatMoney(number)}`
    : `${prefix}${formatCount(number)}`;
}

function DashboardReport() {
  const initialPeriod = useMemo(() => getCurrentYearMonth(), []);
  const initialWeek = useMemo(() => getIsoWeekInfo(new Date()), []);
  const [selectedPeriodType, setSelectedPeriodType] = useState('month');
  const [selectedYear, setSelectedYear] = useState(initialPeriod.year);
  const [selectedMonth, setSelectedMonth] = useState(initialPeriod.month);
  const [selectedWeek, setSelectedWeek] = useState(initialWeek.week);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const year = Number(selectedYear);
  const month = Number(selectedMonth);
  const week = Number(selectedWeek);

  const yearOptions = useMemo(() => {
    const options = [];
    for (let optionYear = initialPeriod.year; optionYear >= initialPeriod.year - 10; optionYear -= 1) {
      options.push(optionYear);
    }
    return options;
  }, [initialPeriod.year]);

  const weekOptions = useMemo(() => {
    const maxWeek = getIsoWeeksInYear(year);
    return Array.from({ length: maxWeek }, (_, index) => index + 1);
  }, [year]);

  useEffect(() => {
    const maxWeek = getIsoWeeksInYear(year);
    if (week > maxWeek) {
      setSelectedWeek(maxWeek);
    }
  }, [week, year]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setErrMsg('');
    try {
      const params = { periodType: selectedPeriodType, year };
      if (selectedPeriodType === 'week') {
        params.week = week;
      } else {
        params.month = month;
      }

      const { data } = await api.get('/reports/dashboard', {
        params,
      });
      setDashboard(data || null);
    } catch (error) {
      console.error('Dashboard-Bericht konnte nicht geladen werden:', error);
      setErrMsg('Dashboard-Bericht konnte nicht geladen werden.');
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [month, selectedPeriodType, week, year]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const metricRows = useMemo(() => {
    const current = dashboard?.periods?.current;
    const previous = dashboard?.periods?.previous;
    if (!current || !previous) {
      return [];
    }

    return [
      {
        label: 'Angebote geschrieben',
        type: 'count',
        current: current.offersCreated,
        previous: previous.offersCreated,
      },
      {
        label: 'Angebote zu Aufträgen',
        type: 'count',
        current: current.offersConvertedToOrders,
        previous: previous.offersConvertedToOrders,
      },
      {
        label: 'Noch offene Angebote',
        type: 'count',
        current: current.offersOpen,
        previous: previous.offersOpen,
      },
      {
        label: 'Angebotswert netto',
        type: 'money',
        current: current.offerValueNet,
        previous: previous.offerValueNet,
      },
      {
        label: 'Auftragswert netto',
        type: 'money',
        current: current.orderValueNet,
        previous: previous.orderValueNet,
      },
      {
        label: 'Rechnungswert netto',
        type: 'money',
        current: current.invoiceValueNet,
        previous: previous.invoiceValueNet,
      },
      {
        label: 'Gutschriftwert netto',
        type: 'money',
        current: current.creditNoteValueNet,
        previous: previous.creditNoteValueNet,
      },
    ].map((row) => ({
      ...row,
      delta: Number(row.current || 0) - Number(row.previous || 0),
    }));
  }, [dashboard]);

  const comparison = dashboard?.yearlyInvoiceNetComparison;
  const accountingBreakdown = dashboard?.invoiceAccountingBreakdown;
  const periodAccountingBreakdown = dashboard?.invoiceAccountingPeriodBreakdown;

  const buildDashboardParams = () => {
    const params = { periodType: selectedPeriodType, year };
    if (selectedPeriodType === 'week') {
      params.week = week;
    } else {
      params.month = month;
    }
    return params;
  };

  const handleDownloadPDF = async () => {
    if (!dashboard || loading) {
      alert('Bitte zuerst Dashboard-Daten laden.');
      return;
    }

    const pdfTab = window.open('', '_blank');

    try {
      const response = await api.get('/reports/dashboard/pdf', {
        params: buildDashboardParams(),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (pdfTab) {
        pdfTab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      if (pdfTab) pdfTab.close();
      console.error('Dashboard-PDF konnte nicht geladen werden:', error);
      alert('Dashboard-PDF konnte nicht erstellt werden.');
    }
  };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <Helmet>
        <title>Bericht: Dashboard</title>
      </Helmet>

      <h2>📊 Dashboard</h2>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          fetchDashboard();
        }}
        style={{
          ...cardStyle,
          width: '100%',
          maxWidth: 'none',
          margin: '0 0 16px 0',
          display: 'block',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ minWidth: 180, flex: '0 0 180px' }}>
            <label htmlFor="dashboardPeriodType" style={{ display: 'block', marginBottom: 6 }}>
              Zeitraumart
            </label>
            <select
              id="dashboardPeriodType"
              value={selectedPeriodType}
              onChange={(event) => setSelectedPeriodType(event.target.value)}
              style={{ width: '100%', margin: 0, marginBottom: 0 }}
            >
              <option value="month">Monat</option>
              <option value="week">Kalenderwoche</option>
            </select>
          </div>
          <div style={{ minWidth: 160, flex: '0 0 160px' }}>
            <label htmlFor="dashboardYear" style={{ display: 'block', marginBottom: 6 }}>
              Jahr
            </label>
            <select
              id="dashboardYear"
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              style={{ width: '100%', margin: 0, marginBottom: 0 }}
            >
              {yearOptions.map((optionYear) => (
                <option key={optionYear} value={optionYear}>
                  {optionYear}
                </option>
              ))}
            </select>
          </div>
          {selectedPeriodType === 'month' ? (
            <div style={{ minWidth: 220, flex: '0 0 220px' }}>
              <label htmlFor="dashboardMonth" style={{ display: 'block', marginBottom: 6 }}>
                Monat
              </label>
              <select
                id="dashboardMonth"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(Number(event.target.value))}
                style={{ width: '100%', margin: 0, marginBottom: 0 }}
              >
                {MONTH_NAMES.map((monthName, index) => (
                  <option key={monthName} value={index + 1}>
                    {monthName}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div style={{ minWidth: 160, flex: '0 0 160px' }}>
              <label htmlFor="dashboardWeek" style={{ display: 'block', marginBottom: 6 }}>
                Kalenderwoche
              </label>
              <select
                id="dashboardWeek"
                value={selectedWeek}
                onChange={(event) => setSelectedWeek(Number(event.target.value))}
                style={{ width: '100%', margin: 0, marginBottom: 0 }}
              >
                {weekOptions.map((optionWeek) => (
                  <option key={optionWeek} value={optionWeek}>
                    KW {String(optionWeek).padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="submit" disabled={loading} style={{ minHeight: 38 }}>
            {loading ? 'Lädt...' : 'Aktualisieren'}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="button" onClick={handleDownloadPDF} disabled={loading || !dashboard} style={{ minHeight: 38 }}>
              PDF drucken
            </button>
          </div>
        </div>
      </form>

      {errMsg && (
        <div style={{ ...cardStyle, marginBottom: 16, borderColor: '#f0b6b6', color: '#a12828' }}>
          {errMsg}
        </div>
      )}

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ marginBottom: 12, fontWeight: 600 }}>
          Zeitraumvergleich {dashboard?.periods?.current?.label || (selectedPeriodType === 'week' ? `KW ${String(week).padStart(2, '0')} ${year}` : `${MONTH_NAMES[month - 1]} ${year}`)}
        </div>

        {dashboard?.periods?.current && dashboard?.periods?.previous && (
          <div style={{ color: '#555', marginBottom: 12 }}>
            Analysezeitraum: {dashboard.periods.current.label} | Vergleichszeitraum: {dashboard.periods.previous.label}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Kennzahl</th>
                <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>
                  {dashboard?.periods?.current?.label || 'Aktueller Zeitraum'}
                </th>
                <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>
                  {dashboard?.periods?.previous?.label || 'Vergleichszeitraum'}
                </th>
                <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Veränderung</th>
              </tr>
            </thead>
            <tbody>
              {metricRows.map((row) => (
                <tr key={row.label}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee' }}>{row.label}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                    {row.type === 'money' ? formatMoney(row.current) : formatCount(row.current)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                    {row.type === 'money' ? formatMoney(row.previous) : formatCount(row.previous)}
                  </td>
                  <td
                    style={{
                      padding: '10px 12px',
                      textAlign: 'right',
                      borderBottom: '1px solid #eee',
                      color: row.delta > 0 ? '#176b2c' : row.delta < 0 ? '#a12828' : '#444',
                      fontWeight: 600,
                    }}
                  >
                    {formatDelta(row.delta, row.type)}
                  </td>
                </tr>
              ))}
              {metricRows.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                    Keine Daten für den gewählten Zeitraum vorhanden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedPeriodType === 'week' && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ marginBottom: 12, fontWeight: 600 }}>
            Netto-Rechnungswerte {periodAccountingBreakdown?.period?.label || `KW ${String(week).padStart(2, '0')} ${year}`} nach Buchungskonto
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Konto</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Bezeichnung</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Netto</th>
                </tr>
              </thead>
              <tbody>
                {(periodAccountingBreakdown?.rows || []).map((row) => (
                  <tr key={`${row.accountingCode}-${row.accountingCodeName}`}>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{row.accountingCode}</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee' }}>{row.accountingCodeName}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>
                      {formatMoney(row.netTotal)}
                    </td>
                  </tr>
                ))}
                {(periodAccountingBreakdown?.rows || []).length === 0 && !loading && (
                  <tr>
                    <td colSpan={3} style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                      Keine Rechnungswerte nach Buchungskonten fuer diese Kalenderwoche vorhanden.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={2} style={{ textAlign: 'left', padding: '10px 12px', borderTop: '1px solid #ddd' }}>Gesamt</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', borderTop: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                    {formatMoney(periodAccountingBreakdown?.total)}
                  </th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ marginBottom: 12, fontWeight: 600 }}>
          Netto-Rechnungswert pro Monat im Vergleich zum Jahr {year} und den 5 Vorjahren
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Monat</th>
                {(comparison?.years || []).map((comparisonYear) => (
                  <th
                    key={comparisonYear}
                    style={{
                      textAlign: 'right',
                      padding: '10px 12px',
                      borderBottom: '1px solid #ddd',
                      backgroundColor: comparisonYear === year ? '#f3f7ff' : 'transparent',
                    }}
                  >
                    {comparisonYear}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(comparison?.months || []).map((row) => (
                <tr key={row.month}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee' }}>{row.label}</td>
                  {(comparison?.years || []).map((comparisonYear) => (
                    <td
                      key={`${row.month}-${comparisonYear}`}
                      style={{
                        padding: '10px 12px',
                        textAlign: 'right',
                        borderBottom: '1px solid #eee',
                        backgroundColor: comparisonYear === year ? '#f3f7ff' : 'transparent',
                      }}
                    >
                      {formatMoney(row.values?.[comparisonYear])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderTop: '1px solid #ddd' }}>Jahressumme</th>
                {(comparison?.years || []).map((comparisonYear) => (
                  <th
                    key={`total-${comparisonYear}`}
                    style={{
                      textAlign: 'right',
                      padding: '10px 12px',
                      borderTop: '1px solid #ddd',
                      backgroundColor: comparisonYear === year ? '#f3f7ff' : 'transparent',
                    }}
                  >
                    {formatMoney(comparison?.totals?.[comparisonYear])}
                  </th>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ marginBottom: 12, fontWeight: 600 }}>
          Netto-Rechnungswerte {year} nach Buchungskonto
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Konto</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Bezeichnung</th>
                {MONTH_NAMES.map((monthName, index) => (
                  <th key={monthName} style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>
                    {monthName.slice(0, 3)}
                  </th>
                ))}
                <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #ddd' }}>Summe</th>
              </tr>
            </thead>
            <tbody>
              {(accountingBreakdown?.rows || []).map((row) => (
                <tr key={`${row.accountingCode}-${row.accountingCodeName}`}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{row.accountingCode}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #eee' }}>{row.accountingCodeName}</td>
                  {MONTH_NAMES.map((monthName, index) => (
                    <td
                      key={`${row.accountingCode}-${monthName}`}
                      style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}
                    >
                      {formatMoney(row.months?.[index + 1])}
                    </td>
                  ))}
                  <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #eee', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {formatMoney(row.yearTotal)}
                  </td>
                </tr>
              ))}
              {(accountingBreakdown?.rows || []).length === 0 && !loading && (
                <tr>
                  <td colSpan={15} style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                    Keine Rechnungswerte nach Buchungskonten für {year} vorhanden.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2} style={{ textAlign: 'left', padding: '10px 12px', borderTop: '1px solid #ddd' }}>Gesamt</th>
                {MONTH_NAMES.map((monthName, index) => (
                  <th
                    key={`total-${monthName}`}
                    style={{ textAlign: 'right', padding: '10px 12px', borderTop: '1px solid #ddd', whiteSpace: 'nowrap' }}
                  >
                    {formatMoney(accountingBreakdown?.totals?.[index + 1])}
                  </th>
                ))}
                <th style={{ textAlign: 'right', padding: '10px 12px', borderTop: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                  {formatMoney(accountingBreakdown?.totals?.yearTotal)}
                </th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DashboardReport;
